// The effect flipbooks as the renderer sees them: the sheets baked in Blender
// (art/fx, model-integration.md 1g), and where each one sits in the atlas
// build-models.mjs packs them into (scripts/fx-atlas.mjs). src/flipbooks.js
// draws from that atlas; the build reads this table to lay it out, so the two
// can never disagree about where a frame is.
//
// Everything but `slot` mirrors art/fx/fx_manifest.json, and
// flipbooks.test.mjs holds it there: a re-render that moves an anchor or
// retimes a sheet fails the test before it can put a fireball's base a metre
// under the ground.

// The atlas: 1024 px blocks, four across and two down. A sheet is 8 x 8
// frames of 128 px (the muzzle flashes are a 4 x 2 strip in a block's top
// left corner). One texture means one draw for every burst on the map, sorted
// back to front across all of them — smoke over a splash over a fireball, as
// they stand.
export const FX_ATLAS = { width: 4096, height: 2048, block: 1024, framePx: 128 };
// The phones' and software GL's atlas: the same layout at half the size (64 px
// frames), a quarter of the download and of the texture memory.
export const FX_ATLAS_LITE = { width: 2048, height: 1024, block: 512, framePx: 64 };

/**
 * Each sheet: its art/fx file stem, its block in the atlas (column, row),
 * grid, frame count and playback rate, how many metres a frame covers at
 * scale 1, how it blends, and where its emitter sits in a frame (u across, v
 * up from the frame's bottom edge).
 *
 *   alpha         — straight alpha: gas lit neutral grey, tinted in the
 *                   shader (packed premultiplied, so its mips don't grey)
 *   additive      — light on black
 *   premultiplied — fire adds light where alpha is ~0, smoke occludes
 */
export const FX_SHEETS = {
  explosion: {
    file: "explosion", slot: [0, 0], cols: 8, rows: 8, frames: 64, fps: 24,
    frameM: 9, blend: "premultiplied", anchor: [0.5, 0.4], loop: false,
  },
  flak: {
    file: "flak_burst", slot: [1, 0], cols: 8, rows: 8, frames: 64, fps: 24,
    frameM: 5, blend: "premultiplied", anchor: [0.5, 0.48], loop: false,
  },
  smoke: {
    file: "smoke", slot: [2, 0], cols: 8, rows: 8, frames: 64, fps: 24,
    frameM: 5, blend: "alpha", anchor: [0.5, 0.22], loop: false,
  },
  fire: {
    file: "fire", slot: [3, 0], cols: 8, rows: 8, frames: 64, fps: 30,
    frameM: 2.6, blend: "additive", anchor: [0.5, 0.1], loop: true,
  },
  splash: {
    file: "water_splash", slot: [0, 1], cols: 8, rows: 8, frames: 64, fps: 24,
    frameM: 10, blend: "alpha", anchor: [0.5, 0.01], loop: false,
  },
  dust: {
    file: "dust", slot: [1, 1], cols: 8, rows: 8, frames: 64, fps: 24,
    frameM: 6, blend: "alpha", anchor: [0.5, 0.25], loop: false,
  },
  // Eight single frames, not an animation: row 0 side-on (the barrel points
  // +X, the flash blooms right of the anchor), row 1 end-on stars.
  muzzle: {
    file: "muzzle_flash", slot: [2, 1], cols: 4, rows: 2, frames: 8, fps: 0,
    frameM: 2, blend: "additive", anchor: [0.14, 0.5], anchorEndOn: [0.5, 0.5], loop: false,
    sideOn: [0, 1, 2, 3], endOn: [4, 5, 6, 7],
  },
};

export const FX_SHEET_NAMES = Object.keys(FX_SHEETS);

// How each blend is drawn (the shader's `mode`).
export const FX_MODE = { alpha: 0, additive: 1, premultiplied: 2 };

/**
 * Where frame `k` of `sheet` sits in an atlas of layout `atlas`: its
 * top-left pixel (x, y rows down from the top).
 */
export function framePixel(sheet, k, atlas = FX_ATLAS) {
  const s = FX_SHEETS[sheet];
  const col = k % s.cols, row = Math.floor(k / s.cols);
  return {
    x: s.slot[0] * atlas.block + col * atlas.framePx,
    y: s.slot[1] * atlas.block + row * atlas.framePx,
  };
}

/**
 * The texture coordinate of frame `k`'s bottom-left corner (the atlas is
 * uploaded the right way up, v = 1 along its top edge). Layout-free: the
 * lite atlas has the same coordinates.
 */
export function frameUV(sheet, k) {
  const p = framePixel(sheet, k, FX_ATLAS);
  return [p.x / FX_ATLAS.width, 1 - (p.y + FX_ATLAS.framePx) / FX_ATLAS.height];
}

// One frame's extent in texture coordinates.
export const FRAME_U = FX_ATLAS.framePx / FX_ATLAS.width;
export const FRAME_V = FX_ATLAS.framePx / FX_ATLAS.height;
